import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IParams } from '../../utils/interfaces';
import { BASE_API_URL } from '../../utils/utils';
import styles from './publish-history.module.css';

interface HistoryEntry {
  _id: string;
  publishedAt: string;
  publishedBy: string;
  label?: string;
  type?: 'publish' | 'restore';
}

interface DiffRow {
  key: string;
  snapshotValue: string;
  currentValue: string;
  status: 'changed' | 'added' | 'removed';
}

const formatDate = (isoString: string) => {
  return new Date(isoString).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const flattenObject = (obj: any, prefix = ''): Record<string, string> => {
  if (!obj || typeof obj !== 'object') return {};
  return Object.keys(obj).reduce((acc, key) => {
    const val = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(acc, flattenObject(val, newKey));
    } else {
      acc[newKey] = JSON.stringify(val);
    }
    return acc;
  }, {} as Record<string, string>);
};

const IGNORED_KEY_PREFIXES = ['configVersion', 'version'];

const computeDiff = (snapshotConfig: any, currentConfig: any): DiffRow[] => {
  const flat1 = flattenObject(snapshotConfig);
  const flat2 = flattenObject(currentConfig);
  const allKeys = new Set([...Object.keys(flat1), ...Object.keys(flat2)]);
  const rows: DiffRow[] = [];

  allKeys.forEach(key => {
    if (IGNORED_KEY_PREFIXES.some(p => key === p || key.startsWith(`${p}.`))) return;
    const v1 = flat1[key];
    const v2 = flat2[key];
    if (v1 === v2) return;
    if (v1 !== undefined && v2 !== undefined) {
      rows.push({ key, snapshotValue: v1, currentValue: v2, status: 'changed' });
    } else if (v1 !== undefined) {
      // Key exists in snapshot but not in current → restore will ADD this to live
      rows.push({ key, snapshotValue: v1, currentValue: '—', status: 'added' });
    } else {
      // Key exists in current but not in snapshot → restore will REMOVE this from live
      rows.push({ key, snapshotValue: '—', currentValue: v2, status: 'removed' });
    }
  });

  return rows.sort((a, b) => a.key.localeCompare(b.key));
};

const statusClass: Record<DiffRow['status'], string> = {
  changed: styles.diffChanged,
  added: styles.diffAdded,
  removed: styles.diffRemoved
};

const DiffModal = ({
  snapshotDate,
  diff,
  onClose
}: {
  snapshotDate: string;
  diff: DiffRow[] | null;
  onClose: () => void;
}) => (
  <div className={styles.modalOverlay} onClick={onClose}>
    <div className={styles.modal} onClick={e => e.stopPropagation()}>
      <div className={styles.modalHeader}>
        <span>Diff — current live vs selected version ({snapshotDate})</span>
        <button className={styles.modalClose} onClick={onClose}>
          ✕
        </button>
      </div>
      <div className={styles.modalBody}>
        {diff === null ? (
          <p>Loading…</p>
        ) : diff.length === 0 ? (
          <p className={styles.noDiff}>No differences found.</p>
        ) : (
          <table className={styles.diffTable}>
            <thead>
              <tr>
                <th>Key</th>
                <th>Current live</th>
                <th>Selected version</th>
              </tr>
            </thead>
            <tbody>
              {diff.map(row => (
                <tr key={row.key} className={statusClass[row.status]}>
                  <td>{row.key}</td>
                  <td>{row.currentValue}</td>
                  <td>{row.snapshotValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </div>
);

const PublishHistory = () => {
  const { publisherId = '', domain = '' } = useParams<IParams>();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [diffState, setDiffState] = useState<{ entry: HistoryEntry; diff: DiffRow[] | null } | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_API_URL}/${publisherId}/${domain}/publish-history`);
      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch publish history', err);
    } finally {
      setLoading(false);
    }
  }, [publisherId, domain]);

  useEffect(() => {
    fetchHistory();
  }, [publisherId, domain, fetchHistory]);

  const handleRestore = async (id: string) => {
    if (!window.confirm('Restore this version? The current live config will be overwritten.')) return;
    try {
      const res = await fetch(`${BASE_API_URL}/${publisherId}/${domain}/publish-history/${id}/restore`, {
        method: 'POST'
      });
      if (res.ok) {
        alert('Config restored successfully.');
        await fetchHistory();
      } else {
        const data = await res.json();
        alert(`Restore failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert('Restore failed: network error.');
    }
  };

  const handleViewDiff = async (entry: HistoryEntry) => {
    setDiffState({ entry, diff: null });
    try {
      const [snapshotRes, currentRes] = await Promise.all([
        fetch(`${BASE_API_URL}/${publisherId}/${domain}/publish-history/${entry._id}`),
        fetch(`${BASE_API_URL}/${publisherId}/${domain}/config?fullConfig`, { cache: 'no-store' })
      ]);
      const [snapshotData, currentData] = await Promise.all([snapshotRes.json(), currentRes.json()]);
      setDiffState({ entry, diff: computeDiff(snapshotData.config, currentData.config) });
    } catch (err) {
      console.error('Failed to load diff', err);
      setDiffState(null);
    }
  };

  if (loading) return <div className={styles.wrapper}>Loading...</div>;

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.title}>Publish History — {domain}</h2>
      {history.length === 0 ? (
        <p>No publish history yet.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date / Time</th>
              <th>Published By</th>
              <th>Label</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {history.map(entry => {
              const isRestore = entry.type === 'restore';
              return (
                <tr key={entry._id}>
                  <td>{formatDate(entry.publishedAt)}</td>
                  <td>{entry.publishedBy}</td>
                  <td>
                    <span className={isRestore ? styles.labelRestore : styles.labelPublish}>{entry.label || '—'}</span>
                  </td>
                  <td>
                    <button className={styles.diffButton} onClick={() => handleViewDiff(entry)}>
                      View Diff
                    </button>
                    <button className={styles.restoreButton} onClick={() => handleRestore(entry._id)}>
                      Restore
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {diffState && (
        <DiffModal
          snapshotDate={formatDate(diffState.entry.publishedAt)}
          diff={diffState.diff}
          onClose={() => setDiffState(null)}
        />
      )}
    </div>
  );
};

export default PublishHistory;
